import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import { setPageMeta, JsonLd, articleSchema, faqSchema, breadcrumbSchema, speakableSchema } from "../lib/schema";
import { buildWhatsAppLink } from "../lib/whatsapp";

function renderContent(content) {
  // Lightweight markdown: handle ## headings, **bold**, [text](url) links, paragraphs.
  const blocks = content.split(/\n{2,}/);
  return blocks.map((block, i) => {
    if (block.startsWith("## ")) {
      return (
        <h2 key={i} className="font-serif text-3xl md:text-4xl mt-14 mb-6 tracking-tight">
          {block.replace(/^##\s+/, "")}
        </h2>
      );
    }
    const html = block
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-[#C9A96E] underline underline-offset-4 hover:text-[#A68A52]">$1</a>');
    return (
      <p
        key={i}
        className="mt-6 text-lg text-[#1A1A1A] leading-[1.75]"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  });
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api
      .getBlogPost(slug)
      .then((p) => {
        setPost(p);
        setPageMeta({
          title: `${p.title} | MPS Blog`,
          description: p.excerpt,
          canonical: `https://privateshopper.co.uk/blog/${p.slug}`,
          image: p.cover_image,
        });
      })
      .catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <div className="container-mps py-40 text-center">
        <div className="overline text-[#C9A96E] mb-3">404</div>
        <h1 className="font-serif text-5xl mb-6">Post not found</h1>
        <Link to="/blog" className="text-[#C9A96E] hover:underline">Back to journal</Link>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="container-mps py-40">
        <div className="h-8 w-32 bg-[#E5E0D8]/50 animate-pulse mb-6" />
        <div className="h-14 w-3/4 bg-[#E5E0D8]/50 animate-pulse mb-3" />
        <div className="h-14 w-2/3 bg-[#E5E0D8]/50 animate-pulse" />
      </div>
    );
  }

  const date = new Date(post.published_at).toLocaleDateString("en-GB", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <>
      <JsonLd
        data={articleSchema({
          title: post.title,
          slug: post.slug,
          excerpt: post.excerpt,
          publishedAt: post.published_at,
          cover: post.cover_image,
        })}
      />
      <JsonLd data={breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Blog", url: "/blog" },
        { name: post.title },
      ])} />
      <JsonLd data={speakableSchema(["[data-testid='blog-post-title']", "[data-testid='blog-post-body']"])} />
      {post.schema_faq?.length > 0 && <JsonLd data={faqSchema(post.schema_faq)} />}

      <article className="bg-[#F8F5F0]">
        {/* Hero */}
        <div className="relative pt-40 pb-20 md:pt-48 md:pb-24 bg-[#0A0A0A] text-[#F8F5F0] overflow-hidden">
          {post.cover_image && (
            <>
              <div
                className="absolute inset-0 bg-cover bg-center opacity-50"
                style={{ backgroundImage: `url('${post.cover_image}')` }}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/70 to-[#0A0A0A]" />
            </>
          )}
          <div className="relative container-mps max-w-4xl mx-auto">
            <Link
              to="/blog"
              data-testid="blog-back-link"
              className="inline-flex items-center gap-2 overline text-[#C9A96E] hover:text-[#F8F5F0] mb-6"
            >
              <ArrowLeft size={14} /> Back to Journal
            </Link>
            <div className="flex items-center gap-4 mb-6 text-xs text-[#F8F5F0]/60">
              <span className="overline text-[#C9A96E]">{post.category}</span>
              <span>·</span>
              <span>{date}</span>
            </div>
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="font-serif text-4xl md:text-6xl tracking-tight leading-[1.05]"
              data-testid="blog-post-title"
            >
              {post.title}
            </motion.h1>
            <p className="mt-8 text-lg text-[#F8F5F0]/80 max-w-2xl">{post.excerpt}</p>
          </div>
        </div>

        {/* Body */}
        <div className="container-mps max-w-3xl mx-auto py-16 md:py-24">
          <div data-testid="blog-post-body" className="prose-mps">
            {renderContent(post.content)}
          </div>

          {/* CTA */}
          <div className="mt-16 p-10 bg-[#0A0A0A] text-[#F8F5F0]">
            <div className="overline text-[#C9A96E] mb-3">Ready to order?</div>
            <h3 className="font-serif text-3xl md:text-4xl mb-4">
              From the UK to your door in 7 to 10 days.
            </h3>
            <p className="text-[#F8F5F0]/70 max-w-lg mb-8">
              A real concierge will read your request and quote you in hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/order"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#C9A96E] text-[#0A0A0A] text-sm uppercase tracking-[0.18em] hover:bg-[#A68A52]"
              >
                Place an Order <ArrowUpRight size={14} />
              </Link>
              <a
                href={buildWhatsAppLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-[#C9A96E]/60 text-[#F8F5F0] text-sm uppercase tracking-[0.18em] hover:bg-[#C9A96E] hover:text-[#0A0A0A]"
              >
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </article>
    </>
  );
}
